import React from 'react';
import rpcShared from '@rockpapercoin/rpc-shared';
import { Typography } from '@mui/material';
import {
	DefaultEventProps,
	UserForContractPastDueEventFeed,
	userIsSenderOnContractEvent,
	userIsAssignedMemberOrVendorAdminOnContractEvent,
} from './helpers';
// Lib
import { getDetailLink, linkObject } from '../../../lib/helpers';
import { LinkObjectType } from '../../../lib/helpers/types';

type GetContractDueSoonEventPropsProps = {
	eventProps: DefaultEventProps;
	user: UserForContractPastDueEventFeed;
	resendContract: ( id: string ) => Promise<void>;
	openModal: ( onGotItOverride?: () => void ) => void;
};

const getContractDueSoonProps = ( {
	eventProps,
	user,
	resendContract,
	openModal,
}: GetContractDueSoonEventPropsProps ) => {
	const { event } = eventProps;
	if ( !event.contract ) {
		throw new Error( `Contract missing on ContractDueSoon event ${ event.id }` );
	}
	if ( !user || user.isLoggedIn === false ) {
		// user logged out and this component is re-rendering
		return;
	}

	eventProps.icon = 'contract-overdue';

	/* When you're signed in as the customer and looking at your own event feed, or looking at the
	event feed on the org detail page */
	if ( rpcShared.contracts.isCustomer( event.contract, user?.user?.id ) ) {
		eventProps.message = (
			<Typography variant='body1'>
				{ linkObject( event.contract ) } from { linkObject( event.vendor ) } is due
				soon.
			</Typography>
		);
		eventProps.links = [
			{
				type: event.resolvedAt ? 'secondary' : 'primary',
				text: event.resolvedAt ? 'View' : 'Sign',
				link: getDetailLink( {
					object: event.contract,
					type: LinkObjectType.Contract,
				} ),
			},
		];
		return eventProps;
	}

	// If we're from the sending vendor
	if (
		userIsSenderOnContractEvent( event, user ) ||
		userIsAssignedMemberOrVendorAdminOnContractEvent( event, user )
	) {
		eventProps.message = (
			<Typography variant='body1'>
				{ linkObject( event.contract ) } is due soon. Resend?
			</Typography>
		);
		eventProps.links = [
			{
				type: event.resolvedAt ? 'secondary' : 'primary',
				text: event.resolvedAt ? 'View' : 'Resend',
				link: event.resolvedAt
					? getDetailLink( {
						object: event.contract,
						type: LinkObjectType.Contract,
					  } )
					: null,
				onClick: event.resolvedAt
					? undefined
					: () => {
						const contractId = event.contract?.id;
						if ( !contractId ) {
							return;
						}
						if ( event.contract?.automatedRemindersEnabled === false ) {
							openModal( () => resendContract( contractId ) );
							return;
						}
						resendContract( contractId );
					  },
			},
		];
		return eventProps;
	}

	// When you're signed in as the APPA and looking at this event in your own event feed or in tabs
	if (
		rpcShared.contracts.isAssignedPlannerOrAdminOfCustomersPlannerOrg(
			event.contract,
			user
		)
	) {
		eventProps.message = (
			<Typography variant='body1'>
				{ linkObject( event.contract ) } is due soon.
			</Typography>
		);
		eventProps.links = [
			{
				type: 'secondary',
				text: 'View',
				link: getDetailLink( {
					object: event.contract,
					type: LinkObjectType.Contract,
				} ),
			},
		];
		return eventProps;
	}

	// eslint-disable-next-line max-len
	throw new Error(
		`getContractPastDueEventProps unable to determine user role.\n event:\n ${ JSON.stringify(
			event,
			null,
			2
		) }\n user: \n ${ user } `
	);
};

export default getContractDueSoonProps;
